import React from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import * as instructorActions from '../../_actions/roster.actions'
import _ from 'lodash'
import BigCalendar from 'react-big-calendar'
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop'
import { DndProvider } from 'react-dnd'
import { Dialog } from 'primereact/dialog'
import { toast } from 'react-toastify'
import Moment from 'moment'
import { extendMoment } from 'moment-range'
import PreviewToolbar from './PreviewToolbar'
import Filter from './Filter'
import RosterForm from '../etimeform/RosterForm'
import RosterPreview from '../etimeform/RosterPreview'
import ColorCustomization from '../settings/theme/ColorCustomization'
import RosterManagment from './RosterManagment'
import RosterTemplate from './RosterTemplate'
import TimeUtils from '../../utils/TimeUtils'
import { viewConstants } from '../../_constants/view.constants'

import './css/style.css'
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css'

// ── Per-employee accent colours ────────────────────────────────────────────────
// Runtime cache: employeeID → generated HSL accent colour.
const _employeeColorCache = {}

/**
 * Deterministically generates a vivid HSL colour from any employeeID string/number.
 * Uses the golden-ratio hue spread so sequential IDs (1001, 1002, …) land on
 * visually distant hues rather than adjacent ones.
 * The result is cached so the same employee always gets the same colour within
 * a browser session.
 */
function employeeAccentColor(employeeID) {
    if (!employeeID) return 'hsl(210, 80%, 45%)'
    const key = String(employeeID)
    if (_employeeColorCache[key]) return _employeeColorCache[key]

    // Hash the ID string to a 32-bit integer
    let hash = 0
    for (let i = 0; i < key.length; i++) {
        hash = Math.imul(hash ^ key.charCodeAt(i), 2654435761) >>> 0 // Knuth multiplicative hash
    }

    // Map to hue using golden-ratio offset so nearby integers spread far apart
    const hue = (hash * 137.508) % 360  // 137.508° = golden angle
    const colour = `hsl(${hue.toFixed(0)}, 72%, 42%)`
    _employeeColorCache[key] = colour
    return colour
}

/**
 * Darkens an hsl() colour string by reducing lightness, or a hex by ratio.
 */
function darkenColor(colour, amount = 10) {
    // Handle hsl(h, s%, l%) strings produced by employeeAccentColor
    const hslMatch = colour.match(/hsl\((\d+(?:\.\d+)?),\s*(\d+)%,\s*(\d+)%\)/)
    if (hslMatch) {
        const h = hslMatch[1]
        const s = hslMatch[2]
        const l = Math.max(0, Number(hslMatch[3]) - amount)
        return `hsl(${h}, ${s}%, ${l}%)`
    }
    // Fallback for hex
    const c = colour.replace('#', '')
    const num = parseInt(c.length === 3 ? c.split('').map(x => x + x).join('') : c, 16)
    const ratio = amount / 100
    const r = Math.max(0, ((num >> 16) & 0xff) * (1 - ratio)) | 0
    const g = Math.max(0, ((num >> 8) & 0xff) * (1 - ratio)) | 0
    const b = Math.max(0, (num & 0xff) * (1 - ratio)) | 0
    return `#${[r, g, b].map(v => v.toString(16).padStart(2, '0')).join('')}`
}

/**
 * Returns a very light tint of the accent colour suitable for card backgrounds.
 * Keeps the same hue but pushes saturation down and lightness up so the text
 * stays readable while still being visually distinct between employees.
 */
function tintColor(colour) {
    const hslMatch = colour.match(/hsl\((\d+(?:\.\d+)?),\s*(\d+)%,\s*(\d+)%\)/)
    if (hslMatch) {
        const h = hslMatch[1]
        // Low saturation + very high lightness = soft pastel tint
        return `hsl(${h}, 60%, 94%)`
    }
    return '#f5f5f5'
}



toast.configure()

const moment = extendMoment(Moment)
const localizer = BigCalendar.momentLocalizer(moment)
const DragAndDropCalendar = withDragAndDrop(BigCalendar, DndProvider)

const NORMALWORKING_ACTION = 99

class RosterPreviewPage extends React.PureComponent {
    state = {
        // 'month' | 'week' — the single source of truth for which view is active
        currentView: 'month',
        // UI state
        showFilters: false,
        modalActionType: '',   // '' | 'create' | 'update'
        event: {},
        showLegend: false,
        content: 'roster',     // 'roster' | 'manager' | 'template'
    }

    // ── Lifecycle ──────────────────────────────────────────────────────────────
    componentDidMount() {
        const { events } = this.props
        if (!events || events.length === 0) {
            this.props.actions.fetchUserETime()
        }
    }

    // ── View switching ─────────────────────────────────────────────────────────
    handleViewChange = (view) => {
        // Reset filters on every view switch
        this._resetFilters()

        if (view === 'week') {
            this.setState({ currentView: 'week' })
            const options = { range: viewConstants.WEEK, time: new Date() }
            this.props.actions.fetchUpdateWorkETime(options)
        } else {
            this.setState({ currentView: 'month' })
            this.props.actions.fetchUserETime()
        }
    }

    _resetFilters = () => {
        this.setState({ showFilters: false })
        this.props.actions.selectEmployee('')
        this.props.actions.selectEmployees([])
        this.props.actions.selectBranches([])
        this.props.actions.selectDepartments([])
        this.props.actions.selectDateRangeFilter({ startDate: null, endDate: null, hasActive: false })
    }

    // ── Filter panel ───────────────────────────────────────────────────────────
    toggleFilters = () => this.setState(s => ({ showFilters: !s.showFilters }))

    hasActiveFilters = () => {
        const { selectedEmployees, selectedBranches, selectedDepartments, hasActiveDateRangeFilter } = this.props
        return !!(
            selectedEmployees?.length > 0 ||
            selectedBranches?.length > 0 ||
            selectedDepartments?.length > 0 ||
            hasActiveDateRangeFilter
        )
    }

    // ── Modal ──────────────────────────────────────────────────────────────────
    onFormHide = () => this.setState({ modalActionType: '' })

    // ── Event handlers ─────────────────────────────────────────────────────────
    handleSelectSlot = (e) => {
        if (!_.isEmpty(this.props.selectedEmployees)) {
            this.newEvent(e)
        }
    }

    newEvent = (event) => {
        const eTimeRequest = {
            id: '',
            title: '',
            actionType: { label: 'Normal working', value: NORMALWORKING_ACTION },
            allDay: event.slots.length === 1,
            dateFrom: event.start,
            dateTo: event.slots.length > 1 ? event.end : undefined,
            draggable: true,
            status: 0,
            startTime: '09:00',
            endTime: '17:30',
            duration: '',
            break: event.break || '0',
            note: '',
            existing: false,
        }
        this.setState({ event: eTimeRequest, modalActionType: 'create' })
    }

    renderEventDetail = (event) => {
        const eTimeRequest = {
            id: event.id,
            title: event.Title || event.title,
            actionType: _.find(this.props.actionTypes, { value: event.ActionType }),
            allDay: event.allDay,
            dateFrom: event.start,
            startTime: TimeUtils.extractTime(event.start),
            endTime: event.end ? TimeUtils.extractTime(event.end) : '17:30',
            duration: event.ActionHours || '',
            break: event.Break || 0,
            Note: event.Note || '',
            existing: true,
            status: event.Status,
            employeeID: event.EmployeeID,
            EmployeeName: event.EmployeeName,
            hours: event.hours || 0,
            source: event.source,
        }
        this.setState({ event: eTimeRequest, modalActionType: 'update' })
    }

    moveEvent = ({ event, start, end, isAllDay: droppedOnAllDaySlot }) => {
        const { currentView } = this.state
        if (event.source === 'timesheet') {
            toast.warning('Read only event!', { position: 'top-center' })
            return
        }

        let allDay = event.allDay
        if (!event.allDay && droppedOnAllDaySlot) allDay = true
        else if (event.allDay && !droppedOnAllDaySlot) allDay = false

        if (currentView === 'week') {
            const payload = {
                PayrollEmpRosterAutoID: event.id,
                PayrollEmpRosterEmployeeID: event.EmployeeID,
                PayrollEmpRosterStartDate: start,
                PayrollEmpRosterEndDate: end,
                PayrollEmpRosterActionNotes: event.Note || '',
                PayrollEmpRosterBreakMinute: event.Break,
                PayrollEmpRosterDispTitle: event.Title,
            }
            this.props.actions.updateWeekEvent(payload)
        } else {
            const updatedEvent = { ...event, start, end, allDay }
            this.props.actions.updateEvent(updatedEvent, this.props.selectedEmployee)
        }
    }

    copyEvent = ({ event, start, end }) => {
        const { currentView } = this.state
        if (currentView === 'week') {
            this.props.actions.copyInsertWeekEvents({ id: event.id, start, end })
        } else {
            this.props.actions.copyInsertEvents({ id: event.id, start, end })
        }
    }

    eventPropGetter = (event) => {
        const accent = employeeAccentColor(event.EmployeeID)
        const isDraft = !event.Status || event.Status === 0 || event.Status === false

        // const now = moment()
        // const start = moment(event.start)
        // const end = moment(event.end)
        const opacity = 1

        // if (isDraft) {
        //     return {
        //         style: {
        //             backgroundColor: '#ffffff',
        //             border: `1.5px dashed ${accent}`,
        //             borderLeft: `3px dashed ${accent}`,
        //             color: '#262626',
        //             fontWeight: 600,
        //             fontSize: '12px',
        //             opacity,
        //         },
        //     }
        // }

        return {
            style: {
                backgroundColor: tintColor(accent),
                border: `1px solid ${accent}`,
                borderLeft: `4px solid ${darkenColor(accent)}`,
                color: '#262626',
                fontWeight: 600,
                fontSize: '12px',
                opacity,
            },
        }
    }

    filterDisplayEvents = () => _.map(this.props.displayEvents || [], obj => obj)
    filterDisplayWorkEvents = () => _.map(this.props.displayWorkEvents || [], obj => obj)

    // ── Toolbar renderer (passed to BigCalendar components prop) ───────────────
    renderToolbar = (rbcProps) => {
        const { currentView, showFilters } = this.state
        return (
            <PreviewToolbar
                {...rbcProps}
                currentView={currentView}
                onViewChange={this.handleViewChange}
                showFilters={showFilters}
                onToggleFilters={this.toggleFilters}
                hasActiveFilters={this.hasActiveFilters()}
            >
                <Filter
                    view={currentView}
                    horizontal={true}
                    filterEvents={() => {}}
                    resetEvents={() => {}}
                />
            </PreviewToolbar>
        )
    }

    components = { toolbar: this.renderToolbar }

    render() {
        const { currentView, modalActionType, content } = this.state

        // ── Month calendar ───────────────────────────────────────────────────
        const monthCalendar = (
            <div className="roster-content">
                <div className="calendar-panel">
                    <div className="panel">
                        <div className="panel-body">
                            <div className="calendar">
                                <div className="roster m-2">
                                    <DragAndDropCalendar
                                        popup
                                        // selectable
                                        localizer={localizer}
                                        events={this.filterDisplayEvents()}
                                        // onEventDrop={this.moveEvent}
                                        // resizable
                                        // onEventResize={this.copyEvent}
                                        onSelectEvent={this.renderEventDetail}
                                        // onSelectSlot={this.handleSelectSlot}
                                        views={['month']}
                                        defaultView={BigCalendar.Views.MONTH}
                                        defaultDate={this.props.currentDate}
                                        eventPropGetter={this.eventPropGetter}
                                        titleAccessor="Title"
                                        startAccessor="start"
                                        endAccessor="end"
                                        showMultiDayTimes={true}
                                        // draggableAccessor="draggable"
                                        culture="en-NZ"
                                        tooltipAccessor="Title"
                                        classNameCustom="rbc-calendar"
                                        components={this.components}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )

        // ── Week calendar ────────────────────────────────────────────────────
        const weekCalendar = (
            <div className="workview-roster">
                <div className="roster-content">
                    <div className="calendar-panel">
                        <div className="panel">
                            <div className="panel-body">
                                <div className="calendar">
                                    <div className="example">
                                        <DragAndDropCalendar
                                            popup
                                            // selectable
                                            localizer={localizer}
                                            events={this.filterDisplayWorkEvents()}
                                            // onEventDrop={this.moveEvent}
                                            // resizable
                                            // onEventResize={this.copyEvent}
                                            onSelectEvent={this.renderEventDetail}
                                            // onSelectSlot={this.handleSelectSlot}
                                            views={['roster']}
                                            defaultView={BigCalendar.Views.ROSTER}
                                            defaultDate={this.props.currentWeekDate || new Date()}
                                            eventPropGetter={this.eventPropGetter}
                                            titleAccessor="Title"
                                            startAccessor="start"
                                            endAccessor="end"
                                            showMultiDayTimes={true}
                                            // draggableAccessor="draggable"
                                            culture="en-NZ"
                                            tooltipAccessor="Title"
                                            step={1440}
                                            timeslots={1}
                                            employees={this.props.employeeList}
                                            showHours={true}
                                            components={this.components}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )

        return (
            <div>
                {/* Main calendar — switches between month and week */}
                {content === 'roster' && (currentView === 'month' ? monthCalendar : weekCalendar)}
                {content === 'manager' && <RosterManagment />}
                {content === 'template' && <RosterTemplate />}

                {/* Legend dialog */}
                <Dialog
                    header="Color"
                    visible={this.state.showLegend}
                    onHide={() => this.setState({ showLegend: false })}
                    maximized={false}
                    maximizable={true}
                >
                    <ColorCustomization />
                </Dialog>

                {/* Create shift dialog */}
                <Dialog
                    header="Create roster"
                    visible={modalActionType === 'create'}
                    onHide={this.onFormHide}
                    maximizable={true}
                    resizable={true}
                    breakpoints={{ '960px': '75vw', '640px': '100vw' }}
                    style={{ width: '50vw' }}
                >
                    <RosterForm
                        event={this.state.event}
                        renderContent="roster"
                        selectedEmployees={this.props.selectedEmployees}
                        actionTypes={this.props.actionTypes}
                        showEmployee={true}
                        onHide={this.onFormHide}
                    />
                </Dialog>

                {/* Shift detail preview (read-only or editable) */}
                {modalActionType === 'update' && (
                    <RosterPreview
                        event={this.state.event}
                        onClose={this.onFormHide}
                        editMode={this.props.editMode}
                        onRefresh={() => {
                            const { currentView } = this.state
                            if (currentView === 'week') {
                                const options = { range: viewConstants.WEEK, time: this.props.currentDate || new Date() }
                                this.props.actions.fetchUpdateWorkETime(options)
                            } else {
                                const options = { range: viewConstants.MONTH, time: this.props.currentDate || new Date() }
                                this.props.actions.fetchUserETime(options)
                            }
                        }}
                    />
                )}
            </div>
        )
    }
}

function mapStateToProps(state) {
    return {
        currentDate: state.roster.currentDate,
        currentWeekDate: state.roster.currentWeekDate,
        events: state.roster.events,
        displayEvents: state.roster.displayEvents,
        displayWorkEvents: state.roster.displayWorkEvents,
        displayEmployees: state.roster.displayEmployees,
        employeeList: state.roster.employeeList,
        selectedEmployee: state.roster.selectedEmployee,
        selectedEmployees: state.roster.selectedEmployees,
        selectedBranches: state.roster.selectedBranches,
        selectedDepartments: state.roster.selectedDepartments,
        hasActiveDateRangeFilter: state.roster.hasActiveDateRangeFilter,
        actionTypes: state.ActionTypesReducer.actionTypes,
        editMode: state.roster.editMode,
    }
}

function mapDispatchToProps(dispatch) {
    return { actions: bindActionCreators(instructorActions, dispatch) }
}

export default connect(mapStateToProps, mapDispatchToProps)(RosterPreviewPage)
